import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import svg from "../../utils/svg"
import styles from "./WatermarkPaywall.module.scss"

/**
 * WatermarkPaywall (`/watermark`) — an original, self-contained EXPIRATION /
 * renewal paywall for Private Photo Vault Premium. Paywall type rolled at random
 * this run: EXPIRATION.
 *
 * ── Style directive (read live from #paywalls each run) ─────────────────────
 * The newest @Paywalls-Bot mention this run is `focus on security and privacy`
 * (NOT the reset keyword `default`), so it is an ACTIVE STANDING DIRECTIVE at
 * super-high priority. The whole concept is built to it — a genuine image-
 * security device, told as the one mark that says "only you."
 *
 * ── Concept ─────────────────────────────────────────────────────────────────
 * "Re-ink the mark that says only you." A watermark is a real security device —
 * the anti-forgery guilloche woven into banknotes, passports and certified
 * prints, and, for images specifically, the invisible mark that proves a photo
 * is protected and yours. Here it is the LIVING SEAL that Premium keeps inked
 * across your vault. While your subscription renews, the mark is live and the
 * guard is on watch. When it lapses, the mark goes dormant — your photos are
 * still locked behind your PIN (Basic), nothing is deleted and nothing leaves
 * the phone — but the active guard is paused. Renewing re-inks the mark and puts
 * the guard back on watch. This sells RENEWAL as risk-reversal + continuity, in
 * a calm, precise, mint-room register — NOT fear, NOT a countdown, NOT loss.
 *
 * ── Interaction (the hero mechanic) ─────────────────────────────────────────
 * RE-INK WITH THE ROLLER. The hero is a "certified plate" — a wall of your own
 * photos under a guilloche security watermark. A letterpress ink ROLLER (brayer)
 * parks at the plate's edge; drag it across and the guilloche prints in behind
 * it — dormant grey ahead of the roller, crisp glowing ink behind — each photo
 * clicking to a sealed "only you" state as the roll passes, the guard marks
 * lighting one by one, ending with a VALID foil press. A print/re-ink drag with
 * a physical roller — not a tilt-parallax on a seal (Hallmark), a decryption
 * seam scrubber (Cipher), a pull-cord (Safelight), a plug-into-socket (Current),
 * a converging radial emblem (Ward), a master power switch (Standby) or a
 * faltering heartbeat (Vigil). New to the set.
 *
 * ── Divergence (vs. the 80+ prior concepts) ────────────────────────────────
 * Layout      : a CERTIFIED-PRINT PLATE / security-document sheet — a photo wall
 *               carrying a live guilloche mark, framed by registration marks —
 *               not a card-hero, calendar, device panel, door, scene or grid.
 * Interaction : DRAG-THE-ROLLER to re-ink a security watermark across the wall.
 * Metaphor    : the WATERMARK / guilloche — a genuine image-security device;
 *               the living mark that says a photo is protected and yours.
 * Value frame : AUTHENTICITY + continuity / risk-reversal — the mark is re-
 *               validated for another year; nothing was ever lost.
 * Art dir.    : LIGHT-first security printing — warm archival paper, fine ink-
 *               blue line-work, a gold "VALID" foil (most security paywalls go
 *               dark; this one is a mint-room). Full dark variant included.
 * Register    : calm, precise, quietly premium — reassurance, not fear.
 * CTA         : "Renew my seal" — yearly featured.
 *
 * ── Honest & App Store compliant ────────────────────────────────────────────
 * Every guard woven into the mark is a REAL Private Photo Vault safeguard
 * (encrypted Cloud Vault · Break-In Reports · Private Camera · App Disguise).
 * The expiration copy is honest: on lapse you drop to Basic, which still holds
 * your photos behind your PIN — there is NO data-loss claim, NO "your photos
 * will be exposed/deleted", NO countdown and NO fabricated scarcity. "Save 79%"
 * is exact arithmetic from live product data. Yearly is the featured, default,
 * most-prominent price; monthly is a small secondary line. The 7-day free trial
 * is the real trial. Renders on built-in debug product data with bundled repo
 * photos — no native injection, no external egress.
 */

// The photo wall — bundled, self-contained repo photos (no network egress).
const WALL = [
  "/byheart/m02.jpg",
  "/byheart/m07.jpg",
  "/byheart/m11.jpg",
  "/byheart/m04.jpg",
  "/byheart/m13.jpg",
  "/byheart/m09.jpg",
  "/byheart/m15.jpg",
  "/byheart/m06.jpg",
  "/byheart/m17.jpg",
]

// Real Premium safeguards woven into the mark. Each engages as the seal re-inks.
const GUARDS = [
  {
    key: "cloud",
    Icon: svg.featureIcons.CloudVault,
    name: "Encrypted Cloud Vault",
    line: "Private, encrypted backup",
  },
  {
    key: "breakin",
    Icon: svg.featureIcons.Breakin,
    name: "Break-In Reports",
    line: "See any wrong attempt",
  },
  {
    key: "camera",
    Icon: svg.featureIcons.SecurityPlus,
    name: "Private Camera",
    line: "Shots skip the camera roll",
  },
  {
    key: "disguise",
    Icon: svg.featureIcons.Disguise,
    name: "App Disguise",
    line: "Hide the vault in plain sight",
  },
]

// ── guilloche (spirograph / hypotrochoid) — a real security-print motif ──────
function spiro(R: number, r: number, d: number, cx = 100, cy = 100): string {
  const steps = 720
  const pts: string[] = []
  for (let i = 0; i <= steps; i++) {
    const t = (i / steps) * Math.PI * 2
    const x = (R - r) * Math.cos(t) + d * Math.cos(((R - r) / r) * t)
    const y = (R - r) * Math.sin(t) - d * Math.sin(((R - r) / r) * t)
    pts.push(`${(cx + x).toFixed(2)},${(cy + y).toFixed(2)}`)
  }
  return "M" + pts.join("L") + "Z"
}

const SealMark = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="8.4" stroke="currentColor" strokeWidth="1.5" />
    <circle
      cx="12"
      cy="12"
      r="5"
      stroke="currentColor"
      strokeWidth="1.2"
      opacity="0.6"
    />
    <path
      d="M9.2 12.2 11 14l3.9-4.2"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const LockMark = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect
      x="5.5"
      y="10.6"
      width="13"
      height="9"
      rx="2.2"
      stroke="currentColor"
      strokeWidth="1.7"
    />
    <path
      d="M8.2 10.6V8a3.8 3.8 0 0 1 7.6 0v2.6"
      stroke="currentColor"
      strokeWidth="1.7"
    />
  </svg>
)

const RollerGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect
      x="3.2"
      y="4.5"
      width="14"
      height="6"
      rx="2"
      stroke="currentColor"
      strokeWidth="1.7"
    />
    <path
      d="M13 13.5v2.2a2 2 0 0 1-2 2H9.6"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
    <path
      d="M9.6 15.6v4"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
    />
  </svg>
)

const WatermarkPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  // ── pricing (yearly featured, monthly secondary) ──────────────────────────
  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const trialDays = yearlyProduct?.introPricing?.period ?? 7

  const savePercent = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 79
    return Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
  }, [yearlyProduct, monthlyProduct])

  // "Valid through" — the honest yearly renewal horizon (no scary countdown).
  const validThrough = useMemo(() => {
    const now = new Date()
    const next = new Date(now.getFullYear() + 1, now.getMonth(), now.getDate())
    return next.toLocaleDateString(undefined, {
      month: "short",
      year: "numeric",
    })
  }, [])

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")

  // ── the re-ink state (0 = dormant, 1 = fully sealed) ──────────────────────
  const [ink, setInk] = useState(0)
  const inkRef = useRef(0)
  const plateRef = useRef<HTMLDivElement | null>(null)
  const draggingRef = useRef(false)
  const rafRef = useRef<number | null>(null)

  const setInkClamped = useCallback((v: number) => {
    const c = Math.max(0, Math.min(1, v))
    inkRef.current = c
    setInk(c)
  }, [])

  const sealed = ink > 0.985

  // programmatic roll to a target (for the button + the intro demo)
  const rollTo = useCallback(
    (target: number, ms = 780) => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      if (reduceMotion) {
        setInkClamped(target)
        return
      }
      const from = inkRef.current
      const start = performance.now()
      const tick = (t: number) => {
        const k = Math.min(1, (t - start) / ms)
        const eased = 1 - Math.pow(1 - k, 3)
        setInkClamped(from + (target - from) * eased)
        if (k < 1) rafRef.current = requestAnimationFrame(tick)
      }
      rafRef.current = requestAnimationFrame(tick)
    },
    [reduceMotion, setInkClamped],
  )

  // intro: arrive sealed under reduced motion; otherwise a gentle partial roll
  // that shows the mechanic (inked left, dormant right, roller mid-plate).
  useEffect(() => {
    if (reduceMotion) {
      setInkClamped(1)
      return
    }
    const id = window.setTimeout(() => rollTo(0.4, 1100), 650)
    return () => {
      window.clearTimeout(id)
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const pointerToInk = useCallback(
    (clientX: number) => {
      const el = plateRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      setInkClamped((clientX - rect.left) / rect.width)
    },
    [setInkClamped],
  )

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (sealed) return
      draggingRef.current = true
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
      pointerToInk(e.clientX)
      try {
        navigator.vibrate?.(6)
      } catch {}
    },
    [sealed, pointerToInk],
  )

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!draggingRef.current) return
      pointerToInk(e.clientX)
    },
    [pointerToInk],
  )

  const onPointerUp = useCallback(() => {
    if (!draggingRef.current) return
    draggingRef.current = false
    // forgiving snap: past ~68% completes the seal; otherwise settle where left.
    if (inkRef.current >= 0.68) rollTo(1, 420)
    else if (inkRef.current < 0.06) rollTo(0, 260)
  }, [rollTo])

  const purchase = useCallback(() => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
    else native.postNativeEvent("subscribe")
  }, [plan, yearlyProduct, monthlyProduct])

  const guilloche = useMemo(
    () => ({
      a: spiro(64, 8, 40),
      b: spiro(72, 9, 30),
      c: spiro(56, 7, 46),
    }),
    [],
  )

  const inkPct = ink * 100
  const guardsLit = GUARDS.map(
    (_, i) => ink >= Math.min(0.94, (i + 1) / GUARDS.length) - 0.02,
  )

  return (
    <div className={styles.page}>
      <button
        className={styles.close}
        onClick={() => native.postNativeEvent("close")}
        aria-label="Close"
      >
        <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
          <path
            d="M5 5l14 14M19 5L5 19"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
          />
        </svg>
      </button>

      <div className={styles.scroll}>
        {/* ── header ─────────────────────────────────────────────────────── */}
        <header className={styles.head}>
          <span className={styles.kicker}>
            <span className={styles.kickerDot} aria-hidden="true">
              <SealMark />
            </span>
            Private Photo Vault · Premium
          </span>
          <h1 className={styles.title}>
            Re-ink the mark
            <br />
            that says <span className={styles.titleAccent}>only you.</span>
          </h1>
          <p className={styles.sub}>
            Your Premium seal has gone dormant. Nothing left your vault — your
            photos are still locked behind your PIN. Renewing re-inks the living
            watermark and puts the guard back on watch.
          </p>
        </header>

        {/* ── the certified plate (hero) ─────────────────────────────────── */}
        <div className={styles.plateWrap}>
          <div className={styles.validRow}>
            <span
              className={`${styles.validPip} ${sealed ? styles.live : ""}`}
              aria-hidden="true"
            />
            <span className={styles.validText}>
              {sealed ? (
                <>
                  Seal live · valid through <b>{validThrough}</b>
                </>
              ) : (
                <>Seal dormant · photos safe behind your PIN</>
              )}
            </span>
          </div>

          <div
            className={`${styles.plate} ${sealed ? styles.sealed : ""}`}
            ref={plateRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            role="slider"
            aria-label="Drag to re-ink the security watermark"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(inkPct)}
          >
            {/* registration corner marks */}
            <span className={`${styles.reg} ${styles.tl}`} aria-hidden="true" />
            <span className={`${styles.reg} ${styles.tr}`} aria-hidden="true" />
            <span className={`${styles.reg} ${styles.bl}`} aria-hidden="true" />
            <span className={`${styles.reg} ${styles.br}`} aria-hidden="true" />

            {/* photo wall */}
            <div className={styles.wall}>
              {WALL.map((src, i) => {
                const col = i % 3
                const tileFrac = (col + 0.85) / 3
                const tileSealed = ink >= tileFrac
                return (
                  <div
                    key={src}
                    className={`${styles.tile} ${tileSealed ? styles.tileSealed : ""}`}
                  >
                    <img src={src} alt="" aria-hidden="true" />
                    <span className={styles.tileMark} aria-hidden="true">
                      <LockMark />
                    </span>
                  </div>
                )
              })}
            </div>

            {/* dormant guilloche (faint, whole plate) */}
            <svg
              className={styles.guillDormant}
              viewBox="0 0 200 200"
              preserveAspectRatio="xMidYMid slice"
              aria-hidden="true"
            >
              <path d={guilloche.a} />
              <path d={guilloche.b} />
              <path d={guilloche.c} />
            </svg>

            {/* inked guilloche (glowing) — revealed behind the roller */}
            <svg
              className={styles.guillInk}
              viewBox="0 0 200 200"
              preserveAspectRatio="xMidYMid slice"
              aria-hidden="true"
              style={{ clipPath: `inset(0 ${100 - inkPct}% 0 0)` }}
            >
              <path d={guilloche.a} />
              <path d={guilloche.b} />
              <path d={guilloche.c} />
            </svg>

            {/* the moving ink edge + roller */}
            {!sealed && (
              <div
                className={styles.rollLane}
                style={{ left: `${inkPct}%` }}
                aria-hidden="true"
              >
                <span className={styles.inkEdge} />
                <span className={styles.roller}>
                  <RollerGlyph />
                </span>
              </div>
            )}

            {/* VALID foil press */}
            <div
              className={`${styles.foil} ${sealed ? styles.on : ""}`}
              aria-hidden="true"
            >
              <span className={styles.foilRing}>
                <SealMark />
              </span>
              <span className={styles.foilText}>VALID</span>
            </div>
          </div>

          {/* the mechanic hint / re-ink control */}
          {sealed ? (
            <div className={styles.rolledDone}>
              <SealMark />
              Seal re-inked across your vault
            </div>
          ) : (
            <button
              className={styles.rollBtn}
              onClick={() => rollTo(1, 900)}
              aria-label="Re-ink the seal"
            >
              <RollerGlyph />
              Drag the roller — or tap to re-ink
            </button>
          )}
        </div>

        {/* ── guards woven into the mark ─────────────────────────────────── */}
        <div className={styles.guards}>
          <div className={styles.guardsHead}>Woven into the mark</div>
          <div className={styles.guardGrid}>
            {GUARDS.map(({ key, Icon, name, line }, i) => (
              <div
                key={key}
                className={`${styles.guard} ${guardsLit[i] ? styles.lit : ""}`}
              >
                <span className={styles.guardIcon}>
                  <Icon />
                </span>
                <div className={styles.guardText}>
                  <div className={styles.guardName}>{name}</div>
                  <div className={styles.guardLine}>{line}</div>
                </div>
                <span className={styles.guardPip} aria-hidden="true">
                  {guardsLit[i] ? (
                    <svg viewBox="0 0 24 24" width="13" height="13">
                      <path
                        d="M20 6.5 10 17l-5-4.6"
                        stroke="currentColor"
                        strokeWidth="2.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                      />
                    </svg>
                  ) : (
                    "off"
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* reassurance */}
        <p className={styles.reassure}>
          Renewing re-validates your seal for another year and brings every
          guard back on watch.{" "}
          <b>Your photos are always yours — nothing is ever deleted.</b>
        </p>

        {/* ── pricing (yearly featured) ──────────────────────────────────── */}
        <div className={styles.pricing}>
          <button
            className={`${styles.planYear} ${plan === "yearly" ? styles.sel : ""}`}
            onClick={() => setPlan("yearly")}
            aria-label="Yearly plan"
          >
            <span className={styles.bestBadge}>Best value</span>
            <span className={styles.saveBadge}>Save {savePercent}%</span>
            <div className={styles.planTop}>
              <span className={styles.planName}>Annual seal</span>
              <span className={styles.trialPill}>
                {trialDays}-day free trial
              </span>
            </div>
            <div className={styles.priceRow}>
              <span className={styles.priceBig}>{yearlyPrice}</span>
              <span className={styles.priceUnit}>/ year</span>
            </div>
            <div className={styles.perMonth}>
              Just <b>{yearlyPerMonth}/mo</b>, billed once a year
            </div>
          </button>

          <button
            className={`${styles.planMonth} ${plan === "monthly" ? styles.sel : ""}`}
            onClick={() => setPlan("monthly")}
            aria-label="Monthly plan"
          >
            <span>
              <span className={styles.planMonthL}>Monthly seal</span>
              <span className={styles.planMonthSub}>Billed every month</span>
            </span>
            <span className={styles.planMonthR}>{monthlyPrice}/mo</span>
          </button>
        </div>

        {/* ── CTA ────────────────────────────────────────────────────────── */}
        <button className={styles.cta} onClick={purchase}>
          <SealMark />
          Renew my seal
        </button>

        <p className={styles.terms}>
          {plan === "yearly"
            ? `${trialDays}-day free trial, then ${yearlyPrice}/year. `
            : `${monthlyPrice}/month. `}
          Auto-renews until cancelled · cancel anytime in Settings.
        </p>
        <button
          className={styles.restore}
          onClick={() => native.postNativeEvent("already_purchased")}
        >
          Restore purchase
        </button>
      </div>
    </div>
  )
}

export default WatermarkPaywall
